import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Background,
  BackgroundVariant,
  ReactFlow,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeMouseHandler,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { buildTreeLayoutFromConfig } from '../utils/buildTreeLayoutFromConfig';

import { MindMapNode, type MindMapNodeData } from './MindMapNode';

import type { DashboardConfig, Direction, TreeNodeMetric } from '@repo/shared-types';

const nodeTypes: NodeTypes = {
  mindMapNode: MindMapNode,
};

function directionFromNodeId(nodeId: string): Direction | null {
  if (nodeId.startsWith('in_') || nodeId === 'dir_inbound') return 'INBOUND';
  if (nodeId.startsWith('out_') || nodeId === 'dir_outbound') return 'OUTBOUND';
  return null;
}

export function MindMapCanvas({
  config,
  data,
  direction,
  selectedClassification,
  onClassificationSelect,
  onDirectionSelect,
  disableSplit = false,
  onReactFlowReady,
}: {
  config: DashboardConfig;
  data: Record<string, TreeNodeMetric>;
  direction: Direction;
  selectedClassification: string | null;
  /** Second arg `metricKey` is the v2 drilldown identifier — passed through
   *  so the panel can filter by the exact metric the count used, even when
   *  multiple nodes share the same `classification` (e.g. `all`). */
  onClassificationSelect?: (classification: string | null, metricKey?: string | null) => void;
  onDirectionSelect?: (dir: Direction | null) => void;
  disableSplit?: boolean;
  onReactFlowReady?: (instance: { fitViewToNode: (metricKey: string) => void }) => void;
}) {
  const handleSelect = useCallback(
    (classification: string, nodeId?: string, metricKey?: string) => {
      if (!onClassificationSelect) return;
      const isSame = selectedClassification === classification;
      onClassificationSelect(isSame ? null : classification, isSame ? null : (metricKey ?? null));
      onDirectionSelect?.(isSame ? null : directionFromNodeId(nodeId ?? ''));
    },
    [selectedClassification, onClassificationSelect, onDirectionSelect],
  );

  const handleSubItemClick = useCallback(
    (classification: string, nodeId: string, metricKey?: string) => {
      handleSelect(classification, nodeId, metricKey);
    },
    [handleSelect],
  );

  const layoutResult = useMemo(
    () =>
      buildTreeLayoutFromConfig(
        config.tree,
        data,
        disableSplit ? 'INBOUND' : direction,
        selectedClassification,
      ),
    [config.tree, data, direction, selectedClassification, disableSplit],
  );

  const [localNodes, setLocalNodes] = useNodesState<Node>([]);
  const [localEdges, setLocalEdges] = useEdgesState<Edge>([]);

  const { fitView } = useReactFlow();
  const isInitialFitDoneRef = useRef(false);
  const prevDirectionRef = useRef(direction);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const localNodesRef = useRef(localNodes);

  useEffect(() => {
    localNodesRef.current = localNodes;
  }, [localNodes]);

  useEffect(() => {
    if (!onReactFlowReady) return;
    onReactFlowReady({
      fitViewToNode: (metricKey: string) => {
        const targetNode = localNodesRef.current.find(
          (n) => (n.data as MindMapNodeData).metricKey === metricKey,
        );
        if (!targetNode) return;

        fitView({ nodes: [{ id: targetNode.id }], padding: 0.5, duration: 600 });

        setLocalNodes((nodes) =>
          nodes.map((n) => ({
            ...n,
            data: { ...n.data, isHighlighted: n.id === targetNode.id },
          })),
        );

        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          setLocalNodes((nodes) =>
            nodes.map((n) => ({ ...n, data: { ...n.data, isHighlighted: false } })),
          );
        }, 2000);
      },
    });
  }, [onReactFlowReady, fitView, setLocalNodes]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  // Reset fitView tracking when direction changes (tree structure changes significantly)
  useEffect(() => {
    if (prevDirectionRef.current !== direction) {
      prevDirectionRef.current = direction;
      isInitialFitDoneRef.current = false;
    }
  }, [direction]);

  useEffect(() => {
    setLocalNodes(
      layoutResult.nodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          isEditing: false,
          onSubItemClick: (c: string, mk?: string) => handleSubItemClick(c, n.id, mk),
        },
      })),
    );

    // Fit viewport after nodes are first populated or after direction change
    if (!isInitialFitDoneRef.current && layoutResult.nodes.length > 0) {
      isInitialFitDoneRef.current = true;
      requestAnimationFrame(() => {
        fitView({ padding: 0.12, maxZoom: 1.1 });
      });
    }
  }, [layoutResult.nodes, handleSubItemClick, setLocalNodes, fitView]);

  useEffect(() => {
    setLocalEdges(layoutResult.edges);
  }, [layoutResult.edges, setLocalEdges]);

  const onNodeClick: NodeMouseHandler = useCallback(
    (_, flowNode) => {
      const nodeData = flowNode.data as MindMapNodeData;
      handleSelect(nodeData.classification, flowNode.id, nodeData.metricKey);
    },
    [handleSelect],
  );

  return (
    <div className="relative h-full w-full">
      <ReactFlow
        nodes={localNodes}
        edges={localEdges}
        nodeTypes={nodeTypes}
        onNodeClick={onClassificationSelect ? onNodeClick : undefined}
        fitView
        fitViewOptions={{ padding: 0.12, maxZoom: 1.1 }}
        minZoom={0.15}
        maxZoom={2.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={28}
          size={1}
          className="bg-background! [&>pattern>circle]:fill-muted-foreground/15"
        />
      </ReactFlow>
    </div>
  );
}
